// `@tiptap/core` is not a direct dependency (pnpm keeps it un-hoisted), so import
// Node/mergeAttributes from `@tiptap/react`, which re-exports them from core.
import { Node, mergeAttributes } from '@tiptap/react';

/**
 * Atomic inline node that represents a template token as a single, unbreakable
 * chip (e.g. `{{employeeFullName}}`). Storing it as one atom avoids the classic
 * "split token" problem where an author accidentally styles half a placeholder.
 *
 * Serialization: the chip renders to `<span data-token="X" class="token-chip">
 * {{X}}</span>`. The literal `{{X}}` inside is exactly what the backend
 * Handlebars pass fills at generation time — the surrounding span is cosmetic
 * and harmless in the final PDF.
 *
 * Round-trip: on load, any `span[data-token]` in stored HTML is parsed back into
 * a chip, so editing an existing template keeps the tokens intact.
 */
export const TokenChip = Node.create({
  name: 'tokenChip',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      token: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-token') || '',
        renderHTML: (attributes) => ({ 'data-token': attributes.token }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-token]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const token = node.attrs.token || '';
    return [
      'span',
      mergeAttributes(HTMLAttributes, { class: 'token-chip' }),
      `{{${token}}}`,
    ];
  },

  renderText({ node }) {
    return `{{${node.attrs.token || ''}}}`;
  },

  addCommands() {
    return {
      insertToken:
        (token) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: { token } }),
    };
  },
});

export default TokenChip;
